import { useEffect, useRef, useState } from "react";
import { Send, ShieldCheck, Eye, SmilePlus, ArrowLeft, CalendarCheck, Pencil, Trash2, X, Check } from "lucide-react";
import { chime } from "@/lib/chime";
import { useProfile } from "@/context/ProfileContext";
import { CheckinStrip } from "@/components/messages/CheckinStrip";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const Reactions = ({ m, emoji, canReact, onReact }) => (
  <div className={`flex flex-wrap items-center gap-1 mt-1 ${m.me ? "justify-end" : ""}`}>
    {Object.entries(m.reactions).filter(([, w]) => w.length).map(([e, w]) => (
      <button key={e} onClick={() => canReact && onReact(m.id, e)} data-testid={`reaction-${m.id}-${e}`} className="px-2 py-0.5 rounded-full bg-white border border-[#E6DEC3] text-xs">{e} {w.length}</button>
    ))}
    {canReact && !m.blocked && (
      <Popover>
        <PopoverTrigger data-testid={`react-btn-${m.id}`} aria-label="React" className="p-1 rounded-full text-stone-400 hover:text-[#2C4A3E]"><SmilePlus className="w-4 h-4" /></PopoverTrigger>
        <PopoverContent className="w-auto p-1 flex gap-1 bg-white">
          {emoji.map((e) => <button key={e} onClick={() => onReact(m.id, e)} data-testid={`react-${m.id}-${e}`} className="text-xl p-1 rounded-lg hover:bg-[#F1EADB]">{e}</button>)}
        </PopoverContent>
      </Popover>
    )}
  </div>
);

const EditBox = ({ m, onDone }) => {
  const [v, setV] = useState(m.text);
  const save = async (e) => {
    e.preventDefault();
    try { await http.patch(`/coach-chat/messages/${m.id}`, { text: v }); onDone(true); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={save} className="max-w-[80%] w-full flex gap-1" data-testid={`edit-box-${m.id}`}>
      <input autoFocus value={v} onChange={(e) => setV(e.target.value)} maxLength={1000} data-testid={`edit-input-${m.id}`} className="flex-1 px-4 py-2 rounded-full border border-[#2C4A3E] text-sm focus:outline-none" />
      <button disabled={!v.trim()} aria-label="Save edit" data-testid={`edit-save-${m.id}`} className="w-9 h-9 rounded-full bg-[#2C4A3E] text-white grid place-items-center disabled:opacity-40"><Check className="w-4 h-4" /></button>
      <button type="button" onClick={() => onDone(false)} aria-label="Cancel edit" data-testid={`edit-cancel-${m.id}`} className="w-9 h-9 rounded-full border border-[#E6DEC3] grid place-items-center"><X className="w-4 h-4" /></button>
    </form>
  );
};

const OwnTools = ({ m, onEdit, onChanged }) => {
  const del = async () => {
    if (!window.confirm("Delete this message for everyone?")) return;
    try { await http.delete(`/coach-chat/messages/${m.id}`); onChanged(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <span className="inline-flex gap-1 mr-1 opacity-60 group-hover:opacity-100 transition-opacity">
      <button onClick={onEdit} aria-label="Edit message" data-testid={`edit-msg-${m.id}`} className="p-1 rounded-full hover:bg-[#F1EADB]"><Pencil className="w-3.5 h-3.5" /></button>
      <button onClick={del} aria-label="Delete message" data-testid={`delete-msg-${m.id}`} className="p-1 rounded-full hover:bg-[#FBE4E1] text-[#A33224]"><Trash2 className="w-3.5 h-3.5" /></button>
    </span>
  );
};

const Bubble = ({ m, onChanged, ...rest }) => {
  const [editing, setEditing] = useState(false);
  if (m.deleted) return <div className={`flex ${m.me ? "justify-end" : "justify-start"} bubble-in`} data-testid={`message-${m.id}`}><span className="px-4 py-2 rounded-3xl text-xs italic text-stone-400 border border-dashed border-[#E6DEC3]" data-testid={`message-deleted-${m.id}`}>Message deleted</span></div>;
  if (editing) return <div className="flex justify-end"><EditBox m={m} onDone={(ok) => { setEditing(false); if (ok) onChanged(); }} /></div>;
  return (
  <div className={`group flex flex-col bubble-in ${m.me ? "items-end" : "items-start"}`} data-testid={`message-${m.id}`}>
    {!m.me && <span className="text-[11px] text-stone-500 ml-3 mb-0.5">{m.name} <span className="capitalize">· {m.role}</span></span>}
    {m.card ? <div className="max-w-[80%] px-4 py-3 rounded-2xl text-sm bg-[#FBF3E2] border border-[#F2D49B] text-[#7A4A12] flex items-start gap-2" data-testid={`conf-card-${m.id}`}>
      <CalendarCheck className="w-4 h-4 mt-0.5 shrink-0" /><span>{m.text}</span></div> :
    <div className={`max-w-[80%] px-4 py-2.5 rounded-3xl text-sm whitespace-pre-wrap break-words ${m.blocked ? "bg-[#FBE9E7] text-[#8A3B2E] border border-[#F2C4BC]" : m.me ? "bg-[#2C4A3E] text-white rounded-br-md" : "bg-white border border-[#E6DEC3] text-stone-800 rounded-bl-md"}`}>
      {m.text}
      {m.edited && <span className={`ml-2 text-[10px] ${m.me ? "text-white/60" : "text-stone-400"}`} data-testid={`message-edited-${m.id}`}>edited</span>}
      {m.blocked && <span className="block text-xs mt-1" data-testid={`message-blocked-${m.id}`}>Not sent: {m.reason}</span>}
    </div>}
    <div className="flex items-center">{m.can_edit && <OwnTools m={m} onEdit={() => setEditing(true)} onChanged={onChanged} />}<Reactions m={m} {...rest} /></div>
  </div>
  );
};

export const Conversation = ({ tid, emoji, onBack, onSeen, mini = false }) => {
  const { profile } = useProfile();
  const [d, setD] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const end = useRef(null);
  const count = useRef(0);
  const timer = useRef(null);
  const lastSeen = useRef(null);
  const load = () => http.get(`/coach-chat/threads/${tid}/messages`).then((r) => { setD(r.data); onSeen(); window.dispatchEvent(new Event("coach-chat-seen")); })
    .catch((e) => { if ([403, 404].includes(e.response?.status)) { clearInterval(timer.current); setErr(formatApiError(e)); } });
  useEffect(() => { window.__clOpenThread = tid; return () => { if (window.__clOpenThread === tid) window.__clOpenThread = null; }; }, [tid]);
  useEffect(() => { setD(null); setErr(null); count.current = 0; lastSeen.current = null; load(); timer.current = setInterval(load, 4000); return () => clearInterval(timer.current); }, [tid]); // eslint-disable-line
  useEffect(() => {
    if (!d) return;
    const last = d.messages[d.messages.length - 1];
    if (last && lastSeen.current && last.id !== lastSeen.current && !last.me) chime(0.35);
    lastSeen.current = last?.id || "none";
    if (d.messages.length !== count.current) { count.current = d.messages.length; end.current?.scrollIntoView({ block: "end" }); }
  }, [d]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { const r = await http.post(`/coach-chat/threads/${tid}/messages`, { text }); setText(""); if (r.data.blocked) toast.error(r.data.reason); load(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const react = async (mid, e) => { try { await http.post(`/coach-chat/messages/${mid}/react`, { emoji: e }); load(); } catch (err) { toast.error(formatApiError(err)); } };
  if (err) return <div className="p-10 text-stone-500 text-sm text-center" data-testid="conversation-error">{err}<button onClick={onBack} className="block mx-auto mt-3 underline text-[#2C4A3E]">Back to chats</button></div>;
  if (!d) return <div className="p-10 text-stone-400 text-sm">Loading chat…</div>;
  const lastMine = [...d.messages].reverse().find((m) => m.me && !m.blocked);
  return (
    <div className="flex flex-col h-full" data-testid="conversation">
      <div className="px-5 py-4 border-b border-[#E6DEC3] flex items-center gap-3">
        <button onClick={onBack} className={`${mini ? "" : "md:hidden "}p-1`} aria-label="Back" data-testid="conversation-back"><ArrowLeft className="w-5 h-5" /></button>
        <div className="flex-1 min-w-0"><p className="font-display text-xl text-[#2C4A3E] truncate" data-testid="conversation-title">{d.thread.name}</p>
          {d.thread.about && <span className="inline-block mt-1 mr-2 px-2.5 py-0.5 rounded-full bg-[#FBF3E2] text-[#7A4A12] text-[11px]" data-testid="conversation-about">About: {d.thread.about.join(" & ") || "your student"}</span>}
          <p className="inline text-xs text-stone-500 truncate">{d.thread.members.map((m) => (m.name.split(" ")[0].endsWith(".") ? m.name : m.name.split(" ")[0])).join(", ")}</p></div>
      </div>
      {d.thread.about && <p className="mx-5 mt-3 rounded-2xl bg-[#EEF3EF] text-[#2C4A3E] text-xs px-3 py-2 flex items-start gap-2" data-testid="conversation-private"><ShieldCheck className="w-4 h-4 shrink-0" />Private grown-ups chat. Kids can't see it. Teachers can't share phone numbers, emails or links here.</p>}
      {d.thread.about && !mini && <CheckinStrip other={d.thread.members.find((m) => m.id !== profile.id)} tick={d.messages.filter((m) => m.card).length} next={d.thread.next_slot} showNext={d.thread.show_next} />}
      {(d.thread.monitored || d.read_only) && (
        <p className="mx-5 mt-3 rounded-2xl bg-[#EEF3EF] text-[#2C4A3E] text-xs px-3 py-2 flex items-start gap-2" data-testid="conversation-safety">
          {d.read_only ? <Eye className="w-4 h-4 shrink-0" /> : <ShieldCheck className="w-4 h-4 shrink-0" />}
          {d.read_only ? "You're viewing this chat because your child is in it. Read-only." : "Parent-monitored: parents get alerts when a teacher messages their child and can read this chat. Text only, no video or camera."}
        </p>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-3" data-testid="message-list">
        {!d.messages.length && <p className="text-sm text-stone-400 text-center py-10">Say hello!</p>}
        {d.messages.map((m) => <Bubble key={m.id} m={m} emoji={emoji} canReact={!d.read_only && !m.deleted} onReact={react} onChanged={load} />)}
        {lastMine?.seen_by.length > 0 && <p className="text-[11px] text-stone-400 text-right" data-testid="seen-by">Seen by {lastMine.seen_by.join(", ")}</p>}
        <div ref={end} />
      </div>
      {!d.read_only && (
        <form onSubmit={send} className="p-4 border-t border-[#E6DEC3] flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Write a kind message…" data-testid="message-input"
            className="flex-1 px-4 py-3 rounded-full bg-white border border-[#E6DEC3] text-sm focus:outline-none focus:border-[#2C4A3E]" />
          <button disabled={busy || !text.trim()} data-testid="message-send-btn" aria-label="Send" className="w-12 h-12 rounded-full bg-[#C87D53] text-white grid place-items-center disabled:opacity-50"><Send className="w-4 h-4" /></button>
        </form>
      )}
    </div>
  );
};
